import { Link } from 'react-router-dom';
import usePageMeta from '../../hooks/usePageMeta';
import { BookOpen, Award, Zap, ArrowRight, Star, CheckCircle2 } from 'lucide-react';

export default function TrainingServicesPage() {
  usePageMeta({
    title: 'Training Services – Stratel',
    description: 'Explore Stratel training services for long-term care teams, including onboarding, compliance, communication, and workforce development support.',
    keywords: 'long-term care training, healthcare training, compliance training, onboarding, workforce development',
  });
  const features = [
    {
      icon: BookOpen,
      title: 'Training Built for Long-Term Care',
      description: 'Content is designed around the real responsibilities, standards, and challenges long-term care teams manage every day.',
    },
    // Hidden for now:
    // {
    //   icon: Users,
    //   title: 'Guidance From Real Experts',
    //   description: 'Work with a team that understands long-term care operations and can help you shape the right training approach.',
    // },
    {
      icon: Award,
      title: 'Clear Progress and Completion',
      description: 'Make it easier to see who has started, what has been completed, and where follow-up may be needed.',
    },
    {
      icon: Zap,
      title: 'Flexible Delivery for Your Team',
      description: 'Support training across different teams, locations, and schedules without turning the process into extra admin work.',
    },
  ];

  const programs = [
    {
      name: 'Onboarding Support',
      audience: 'New hires, supervisors, and onboarding teams',
      summary: 'Give new employees a stronger start with training that supports readiness, consistency, and confidence from day one.',
    },
    {
      name: 'Compliance Training',
      audience: 'Care teams, supervisors, and operations leaders',
      summary: 'Strengthen required training efforts with practical content that helps teams stay informed, prepared, and accountable.',
    },
    {
      name: 'Communication and Care Experience',
      audience: 'Resident-facing staff and facility leadership',
      summary: 'Support stronger communication, dignity, and day-to-day service quality across the resident experience.',
    },
    {
      name: 'Custom Training Support',
      audience: 'Organizations with multi-site or team-specific needs',
      summary: 'Build a training approach around your priorities, workforce needs, and operating environment.',
    },
  ];

  const engagementSteps = [
    {
      title: 'Tell Us What You Need',
      desc: 'Share a few details about your organization, team, or training goals to get the conversation started.',
    },
    {
      title: 'We Learn About Your Team',
      desc: 'We take time to understand your staff, priorities, and the kind of support that will be most useful.',
    },
    {
      title: 'You Get a Clear Recommendation',
      desc: 'We recommend a practical training approach that matches your goals, timeline, and operating environment.',
    },
    {
      title: 'We Support the Rollout',
      desc: 'Once you are ready to move forward, Stratel helps you bring training to your team in a manageable way.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-blue-500/30 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <span className="text-sm font-semibold text-blue-100">Training Services for Long-Term Care</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Training Support That Fits the Reality of Long-Term Care
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Stratel helps long-term care organizations strengthen onboarding, compliance, communication, and workforce development with training that is practical, relevant, and easier to manage.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/get-started"
                  className="inline-flex items-center justify-center bg-white text-blue-700 px-8 py-4 rounded-lg font-bold hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Talk With Stratel
                  <ArrowRight size={20} className="ml-2" />
                </Link>
              </div>
              <p className="mt-4 text-sm text-blue-100/90">
                Start with a simple request and we will follow up with the right next steps.
              </p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                    <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                    <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                    <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                    <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                  </div>
                  <p className="text-blue-100 italic">"Five star training experience."</p>
                  {/*<p className="text-blue-200 font-semibold mt-3">- Healthcare Director</p>*/}
                </div>
                <div className="border-t border-white/20 pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-green-300" />
                    <span className="text-blue-100">Relevant training for long-term care settings</span>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-green-300" />
                    <span className="text-blue-100">Simple way to start the conversation</span>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-green-300" />
                    <span className="text-blue-100">Support from planning through delivery</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-secondary-900 mb-16">
          Why Organizations Choose Stratel Training
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <div key={idx} className="bg-gradient-to-br from-white to-secondary-50 rounded-xl shadow-md overflow-hidden border border-secondary-100 p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-14 w-14 rounded-lg bg-blue-100">
                      <Icon className="h-7 w-7 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-secondary-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-secondary-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Program Offerings */}
      <div className="bg-gradient-to-br from-blue-50 to-secondary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 text-center mb-4">
            Training Support for Long-Term Care Teams
          </h2>
          <p className="text-xl text-secondary-600 text-center mb-16 max-w-2xl mx-auto">
            Explore a few of the ways Stratel can support teams, leaders, and organizations across long-term care.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {programs.map((program, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-md p-8 border border-secondary-100">
                <h3 className="text-xl font-bold text-secondary-900 mb-4">
                  {program.name}
                </h3>
                <p className="text-secondary-700 mb-4 leading-relaxed">
                  {program.summary}
                </p>
                <div className="flex items-center gap-4 text-secondary-600 mb-4">
                  <div className="flex items-center gap-2">
                    <Zap size={18} className="text-blue-600" />
                    <span>Best for: {program.audience}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link 
              to="/get-started"
              className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Ask About Training
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </div>

      {/* Training Solutions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
              What It Is Like to Work With Stratel
            </h2>
            <p className="text-xl text-secondary-600 mb-8 leading-relaxed">
              We keep the first step simple, learn what matters most to your team, and help you move toward a training solution that makes sense for your organization.
            </p>
            <ul className="space-y-4 mb-8">
              {[
                'Start with a simple request',
                'Talk through your goals with the Stratel team',
                'Get a training recommendation that fits your organization',
                'Move forward with clear support and next steps',
                'Help your team learn with less friction',
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <span className="text-secondary-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <Link 
              to="/get-started"
              className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start the Conversation
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
          <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl p-12 border border-blue-200">
            <div className="space-y-6">
              {engagementSteps.map((step, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    {idx + 1}
                  </div>
                  <div>
                    <h4 className="font-bold text-secondary-900 mb-1">{step.title}</h4>
                    <p className="text-secondary-600 text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 md:p-16 text-center text-white shadow-xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Explore Training for Your Team?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Tell us a little about your organization and what kind of training support you are looking for. We will take it from there.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/get-started"
              className="inline-flex items-center justify-center bg-white text-blue-700 px-8 py-4 rounded-lg font-bold hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Contact Stratel
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
